import { useEffect, useState } from "react";
import { APP_LOCALE } from "../../config/locale";
import {
  getCashflowCalendar,
  type CashflowCalendar,
  type CashflowCategory,
  type CashflowEvent,
} from "../../tesouraria/tesourariaApi";
import { FinancialKpiCard, formatCurrency } from "../Obras/components/FinancialDashboardShared";

const HORIZON_DAYS = 90;

const CATEGORY_LABELS: Record<CashflowCategory, string> = {
  milestone: "Marco de faturação",
  invoice: "Fatura a receber",
  rent: "Renda",
  loan: "Empréstimo",
  expense: "Despesa",
  tax: "Imposto/Obrigação",
};

const dateFormatter = new Intl.DateTimeFormat(APP_LOCALE, {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

function formatEventDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return dateFormatter.format(date);
}

interface DayGroup {
  key: string;
  label: string;
  events: CashflowEvent[];
}

function groupByDay(events: CashflowEvent[]): DayGroup[] {
  const groups = new Map<string, DayGroup>();
  for (const event of events) {
    const date = new Date(event.date);
    const key = Number.isNaN(date.getTime()) ? event.date : date.toISOString().slice(0, 10);
    let group = groups.get(key);
    if (!group) {
      group = { key, label: formatEventDate(event.date), events: [] };
      groups.set(key, group);
    }
    group.events.push(event);
  }
  return Array.from(groups.values());
}

function EventTypeBadge({ type }: { type: CashflowEvent["type"] }) {
  const isInflow = type === "Inflow";
  const className = isInflow
    ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
    : "bg-destructive/10 text-destructive border-destructive/30";
  return (
    <span className={`inline-flex px-2 py-1 border text-xs ${className}`}>
      {isInflow ? "Entrada" : "Saída"}
    </span>
  );
}

export function CashflowCalendarPanel() {
  const [calendar, setCalendar] = useState<CashflowCalendar | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getCashflowCalendar(HORIZON_DAYS)
      .then(setCalendar)
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Erro ao carregar o calendário de tesouraria."),
      );
  }, []);

  if (error) {
    return <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3">{error}</div>;
  }
  if (!calendar) {
    return <div className="text-secondary-text py-6">A carregar tesouraria…</div>;
  }

  const dayGroups = groupByDay(calendar.events);

  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-foreground" style={{ fontSize: "var(--text-h3)", fontWeight: "var(--font-weight-semi-bold)" }}>
          Calendário a 90 dias
        </h2>
        <p className="text-secondary-text mt-1">
          Recebimentos previstos (marcos de faturação) e pagamentos previstos (prestações de empréstimos) nos próximos 90 dias.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FinancialKpiCard label="Entradas previstas" value={formatCurrency(calendar.totalInflows)} />
        <FinancialKpiCard label="Saídas previstas" value={formatCurrency(calendar.totalOutflows)} />
        <FinancialKpiCard
          label="Saldo projetado"
          value={formatCurrency(calendar.net)}
          hint={calendar.net >= 0 ? "Saldo positivo no horizonte" : "Saldo negativo no horizonte"}
        />
      </div>

      <div className="bg-card border border-border" style={{ borderRadius: "var(--radius-card)" }}>
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-foreground" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
            Movimentos previstos
          </h3>
        </div>
        <div className="p-5">
          {calendar.events.length === 0 ? (
            <div className="text-secondary-text py-2">Sem movimentos previstos nos próximos 90 dias.</div>
          ) : (
            <div className="space-y-6">
              {dayGroups.map((group) => (
                <div key={group.key}>
                  <div
                    className="text-secondary-text text-sm mb-2"
                    style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                  >
                    {group.label}
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border text-left text-secondary-text text-sm">
                        <th className="px-2 py-2">Descrição</th>
                        <th className="px-2 py-2">Tipo</th>
                        <th className="px-2 py-2 text-right">Montante</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.events.map((event, index) => {
                        const isInflow = event.type === "Inflow";
                        return (
                          <tr
                            key={`${group.key}-${index}`}
                            className="border-b border-border last:border-b-0"
                          >
                            <td className="px-2 py-2">
                              <div className="text-foreground">{event.description}</div>
                              <div className="text-secondary-text text-xs mt-0.5">
                                {CATEGORY_LABELS[event.category] ?? event.category}
                                {event.reference && ` · ${event.reference}`}
                              </div>
                            </td>
                            <td className="px-2 py-2">
                              <EventTypeBadge type={event.type} />
                            </td>
                            <td
                              className={`px-2 py-2 text-right ${isInflow ? "text-emerald-700" : "text-destructive"}`}
                              style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                            >
                              {isInflow ? "+" : "−"}
                              {formatCurrency(event.amount)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
